import { ChangeDetectionStrategy, Component, inject, InjectionToken } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import _ from 'lodash';
import { Observable, of } from 'rxjs';
import { IndividualRoles } from 'src/app/components/profile/profile.enum';
import { DialogSelectRoleData, InformationMessage, MessageLevel } from 'src/app/interfaces';
import { RoleInfo } from 'src/app/models/role-info.model';

import { MaybeNull, MaybeUndefined } from 'src/app/utils';
import { NotificationService } from '../../notifications.service';
import { NotificationState } from '../../notifications.state';
import { DialogActionTypes } from '../dialogs.enum';

export const SELECT_ROLE_DIALOG_DATA = new InjectionToken<DialogSelectRoleData>(`SELECT_ROLE_DIALOG_DATA`);

interface SelectedRole {
  med_staff?: boolean;
  researcher?: boolean;
  patient?: boolean;
  doctor?: boolean;
}

interface RoleControl {
  value: boolean;
  disabled: boolean;
}

export interface SelectedRoleForm {
  med_staff?: FormControl<RoleControl>;
  researcher?: FormControl<RoleControl>;
  doctor?: FormControl<RoleControl>;
  patient?: FormControl<RoleControl>;
}

const noRoleInformationMessage: InformationMessage = {
  title: 'PROFILE_UNABLE_CHANGES_SAVE_DIALOG_TITLE',
  level: 'ERROR',
  content: [
    {
      value: 'PROFILE_WITHOUT_ROLE_UPDATE_ERROR_DESCRIPTION',
      type: 'text',
    },
  ],
  replies: [],
};

const removeRoleInformationMessage: InformationMessage = {
  title: 'PROFILE_DELETE_ROLE_TITLE',
  level: 'ERROR',
  content: [
    {
      value: 'PROFILE_DELETE_ROLE_DESCRIPTION',
      type: 'text',
    },
  ],
  replies: [],
};

const removeDoctorRoleInformationMessage: InformationMessage = {
  title: 'PROFILE_DELETE_ROLE_TITLE',
  level: 'ERROR',
  content: [
    {
      value: 'PROFILE_DELETE_PATIENT_DOCTOR_ROLE_DESCRIPTION',
      type: 'text',
    },
  ],
  replies: [],
};

@Component({
  selector: 'app-dialog-individual-role-select',
  templateUrl: './dialog-individual-role-select.component.html',
  styleUrls: ['./dialog-individual-role-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DialogIndividualRoleSelectComponent extends NotificationState {
  protected notificationLevel: MessageLevel = 'INFO';

  public hasUnsavedChanges$: Observable<boolean> = of(false);

  public roles: RoleInfo[] = [];
  public form: FormGroup = new FormGroup({});
  public saveRolesChanges: string = DialogActionTypes.SAVE_ROLES_CHANGES;
  public originalFormValues: SelectedRole = {};

  public readonly modalArguments = inject(SELECT_ROLE_DIALOG_DATA);

  private notificationService = inject(NotificationService);

  public ngOnInit(): void {
    this.initRoles();
  }

  public actionsHandler(action: DialogActionTypes): void {
    this.dialogActions[action]();
  }

  public submit(): void {
    const currentRoles: RoleInfo[] = [];

    this.roles.forEach((role: RoleInfo) => {
      const control: MaybeNull<AbstractControl> = this.form.get(role.value);

      if (control) {
        currentRoles.push({
          ...role,
          isSelected: control.value as boolean,
          isDisabled: false,
        });
      }
    });

    const hasOneRole = currentRoles.reduce((acc, role) => {
      return role.isSelected || acc ? true : false;
    }, false);

    // If the user has no roles selected, show an error message and return the form to the original value
    if (!hasOneRole) {
      this.notificationService.informationDialog(noRoleInformationMessage);
      this.form.setValue(this.originalFormValues);
      return;
    }

    const previousRolesTypes = this.roles.filter((role) => role.isSelected).map((role) => role.value);
    const newRoleTypes = currentRoles.filter((role) => role.isSelected).map((role) => role.value);

    const deletedRoleTypes = previousRolesTypes.filter((role) => !newRoleTypes.includes(role));

    const modalReplies = [
      {
        callback: () => {
          this.roles.forEach((role) => {
            if (deletedRoleTypes.includes(role.value)) {
              const control: MaybeNull<AbstractControl> = this.form.get(role.value);

              if (control) {
                control.setValue(true);
              }
            }
          });
        },
        label: 'BTN_CANCEL_HINT',
      },
      {
        callback: () => this.activeModal.close(currentRoles),
        label: 'BTN_OK_HINT',
      },
    ];

    if (deletedRoleTypes.length > 0 && (deletedRoleTypes.includes('doctor') || deletedRoleTypes.includes('patient'))) {
      removeDoctorRoleInformationMessage.replies = modalReplies;
      this.notificationService.informationDialog(removeDoctorRoleInformationMessage);
      return;
    }

    if (deletedRoleTypes.length > 0 && !(deletedRoleTypes.includes('doctor') || deletedRoleTypes.includes('patient'))) {
      removeRoleInformationMessage.replies = modalReplies;
      this.notificationService.informationDialog(removeRoleInformationMessage);
      return;
    }

    this.activeModal.close(currentRoles);
  }

  public get isFormChange(): boolean {
    return _.isEqual({ ...this.form.value }, this.originalFormValues);
  }

  private initRoles(): void {
    const rolesKey: string[] = Object.keys(IndividualRoles);

    rolesKey.forEach((key: string) => {
      const addedRole: MaybeUndefined<RoleInfo> = this.modalArguments.roles.find(
        (role: RoleInfo) => role.value === key,
      );

      const role: RoleInfo = !addedRole
        ? new RoleInfo(key, IndividualRoles[key] as string)
        : new RoleInfo(
            key,
            IndividualRoles[key] as string,
            addedRole.isSaved,
            addedRole.isSelected,
            addedRole.isDisabled,
          );

      this.roles.push(role);
    });

    this.initForm();
  }

  private initForm(): void {
    this.roles.forEach((role) => {
      this.form.addControl(
        role.value,
        new FormControl({
          value: role.isSelected,
          disabled: role.isDisabled,
        }),
      );
    });

    this.originalFormValues = { ...this.form.value } as SelectedRole;
  }
}
